import React, { useState, useEffect } from "react";
import * as Styled from "./styled";
import Sidemenu from "../../components/sidemenu";
import { functionService } from "../../lib/functions";
import { Select, DatePicker, Dropdown, Menu, Modal, Input, Button, Tooltip, message, notification } from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import ProfileComp from "../profileView/ProfileComp";
import brief from "../../images/brief_svg.svg";
/* ─────────────────────────────────────────────
   Helper: truncate text with max length
───────────────────────────────────────────── */
const truncateText = (text, maxLength = 25) => {
  if (!text) return "-";
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

/* ─────────────────────────────────────────────
   Shared pagination styles
───────────────────────────────────────────── */
const paginationStyles = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 4px",
    borderTop: "1px solid #F0F0F0",
    marginTop: "8px",
  },
  leftSection: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    color: "#6B7280",
  },
  pageControls: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  pageBtn: (active, disabled) => ({
    minWidth: "34px",
    height: "34px",
    padding: "0 10px",
    borderRadius: "8px",
    border: active ? "none" : "1px solid #E5E7EB",
    background: active ? "#4F46E5" : disabled ? "#F9FAFB" : "#FFFFFF",
    color: active ? "#FFFFFF" : disabled ? "#D1D5DB" : "#374151",
    fontWeight: active ? 600 : 400,
    fontSize: "13px",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.15s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: active ? "0 1px 4px rgba(79,70,229,0.3)" : "none",
  }),
  ellipsis: {
    width: "34px",
    height: "34px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#9CA3AF",
    fontSize: "14px",
    userSelect: "none",
  },
  navBtn: (disabled) => ({
    height: "34px",
    padding: "0 14px",
    borderRadius: "8px",
    border: "1px solid #E5E7EB",
    background: disabled ? "#F9FAFB" : "#FFFFFF",
    color: disabled ? "#D1D5DB" : "#374151",
    fontWeight: 500,
    fontSize: "13px",
    cursor: disabled ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    transition: "all 0.15s ease",
  }),
};

/* ─────────────────────────────────────────────
   Inline styles for the update drawer
───────────────────────────────────────────── */
const drawerStyles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.30)",
    zIndex: 999,
    transition: "opacity 0.25s ease",
  },
  panel: (open) => ({
    position: "fixed",
    top: 0,
    right: 0,
    height: "100vh",
    width: open ? 400 : 0,
    overflow: "hidden",
    transition: "width 0.28s cubic-bezier(0.4,0,0.2,1)",
    background: "#ffffff",
    borderLeft: "1px solid #e8e8e8",
    boxShadow: open ? "-6px 0 24px rgba(0,0,0,0.10)" : "none",
    zIndex: 1000,
    display: "flex",
    flexDirection: "column",
  }),
  inner: {
    width: 400,
    padding: "24px 24px 32px",
    boxSizing: "border-box",
    overflowY: "auto",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 0,
  },
  drawerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
    paddingBottom: 16,
    borderBottom: "1px solid #f0f0f0",
  },
  drawerTitle: { fontSize: 17, fontWeight: 600, margin: 0, color: "#111" },
  drawerSubtitle: { fontSize: 12, color: "#999", margin: "4px 0 0", fontFamily: "monospace" },
  closeBtn: {
    width: 30, height: 30, border: "1px solid #e0e0e0", borderRadius: 6,
    background: "transparent", cursor: "pointer", fontSize: 16, color: "#888",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, lineHeight: 1,
  },
  candidateChip: {
    background: "#f7f8fa", borderRadius: 10, padding: "12px 14px", marginBottom: 24,
    display: "flex", gap: 12, alignItems: "center", border: "1px solid #ebebeb",
  },
  avatar: {
    width: 40, height: 40, borderRadius: "50%", background: "#dbeafe",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 13, fontWeight: 600, color: "#1e40af", flexShrink: 0,
  },
  section: { marginBottom: 20 },
  label: {
    fontSize: 12, fontWeight: 600, color: "#555", display: "block",
    marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em",
  },
  hint: { fontSize: 11, color: "#aaa", marginTop: 5 },
  dropzone: (hasFile) => ({
    border: `1.5px dashed ${hasFile ? "#22c55e" : "#d1d5db"}`,
    borderRadius: 10, padding: "18px 16px", textAlign: "center",
    cursor: "pointer", background: hasFile ? "#f0fdf4" : "#fafafa",
    transition: "all 0.2s", display: "block",
  }),
  dropzoneText: { fontSize: 13, color: "#666", margin: "6px 0 0" },
  dropzoneSub: { fontSize: 11, color: "#aaa", margin: "3px 0 0" },
  fileSelectedBadge: {
    marginTop: 8, padding: "7px 12px", background: "#f0fdf4",
    border: "1px solid #bbf7d0", borderRadius: 7, fontSize: 12, color: "#16a34a",
    display: "flex", alignItems: "center", gap: 6,
  },
  divider: { borderTop: "1px solid #f0f0f0", margin: "20px 0" },
  actionRow: { display: "flex", gap: 10, marginTop: "auto", paddingTop: 16 },
  successBanner: {
    marginTop: 14, padding: "10px 14px", background: "#f0fdf4",
    border: "1px solid #bbf7d0", borderRadius: 8, fontSize: 13,
    color: "#16a34a", textAlign: "center", fontWeight: 500,
  },
  editIconBtn: {
    width: 32, height: 32, borderRadius: 6, background: "#DEE2EF",
    cursor: "pointer", fontSize: 15, color: "#2563eb",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    transition: "background 0.15s", border: "none",
  },
};

/* ─────────────────────────────────────────────
   Reusable status badge helpers
───────────────────────────────────────────── */
const PendingBadge = () => (
  <span style={{ color: "#b45309", fontWeight: 600, background: "#fef3c7", padding: "2px 8px", borderRadius: "4px", fontSize: "12px" }}>
    Pending
  </span>
);

const YesBadge = () => (
  <span style={{ color: "#166534", fontWeight: 600, background: "#dcfce7", padding: "2px 8px", borderRadius: "4px", fontSize: "12px" }}>
    Yes
  </span>
);

const NoBadge = () => (
  <span style={{ color: "#991b1b", fontWeight: 600, background: "#fee2e2", padding: "2px 8px", borderRadius: "4px", fontSize: "12px" }}>
    No
  </span>
);

/* ─────────────────────────────────────────────
   Candidate status options (shared)
───────────────────────────────────────────── */
const candidateStatusOptions = [
  "Yet to be shortlisted",
  "Screen Reject",
  "Pre-Interview Back Out",

  // R1
  "Screen Select - Yet to be Interviewed (R1)",
  "Interview Scheduled (R1)",
  "During Interview Back Out (R1)",
  "Interviewed - Feedback Pending (R1)",
  "Interviewed - Rejected (R1)",

  // R2
  "Yet to be Interviewed (R2)",
  "Interview Scheduled (R2)",
  "During Interview Back Out (R2)",
  "Interviewed - Feedback Pending (R2)",
  "Interviewed - Rejected (R2)",

  // R3
  "Yet to be Interviewed (R3)",
  "Interview Scheduled (R3)",
  "During Interview Back Out (R3)",
  "Interviewed - Feedback Pending (R3)",
  "Interviewed - Rejected (R3)",

  // R4
  "Yet to be Interviewed (R4)",
  "Interview Scheduled (R4)",
  "During Interview Back Out (R4)",
  "Interviewed - Feedback Pending (R4)",
  "Interviewed - Rejected (R4)",

  // Final Stage
  "Post Interview Back Out",
  "Final Select",
  "Final Reject",
  "Pre Offer Back Out",

  // Offer Stage
  "Offer yet to be released",
  "Offer released",
  "Post Offer Back Out",

  // Joining Stage
  "Offer Accepted - Yet to join",
  "Offer Accepted - Joined",
  "Offer Accepted - Not Joined",
  "Offer Rejected - Not joined",

  // Other Outcomes
  "Position on hold/closed",
  "Change in requirement from client",
  "Duplicate Profile",
  "Profile Reshared"
];

/* ─────────────────────────────────────────────
   Update Info Drawer Component
───────────────────────────────────────────── */
const UpdateCandidateDrawer = ({ open, candidate, onClose, onSaved }) => {
  const [updateEmail, setUpdateEmail] = useState("");
  const [updateResume, setUpdateResume] = useState(null);
  const [updateAssessmentDatetime, setUpdateAssessmentDatetime] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    if (candidate) {
      setUpdateEmail(candidate.email || "");
      setUpdateResume(null);
      setUpdateAssessmentDatetime(
        candidate.assessment_schedule_datetime
          ? dayjs(candidate.assessment_schedule_datetime)
          : null
      );
      setShowSuccess(false);
      setEmailError("");
    }
  }, [candidate]);

  const getInitials = (first, last) =>
    `${(first || "")[0] || ""}${(last || "")[0] || ""}`.toUpperCase();

  const validateEmail = (val) => {
    if (!val) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return "Enter a valid email";
    return "";
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      notification.error({ message: "File size exceeded", description: "Max 2MB allowed" });
      e.target.value = "";
      return;
    }
    if (file.type !== "application/pdf") {
      notification.error({ message: "Unsupported format", description: "Only PDF files are allowed" });
      e.target.value = "";
      return;
    }
    setUpdateResume(file);
  };

  const handleSave = async () => {
    const emailChanged = updateEmail !== candidate?.email;
    const originalDatetime = candidate?.assessment_schedule_datetime
      ? dayjs(candidate.assessment_schedule_datetime).toISOString()
      : null;
    const newDatetime = updateAssessmentDatetime
      ? updateAssessmentDatetime.toISOString()
      : null;
    const datetimeChanged = originalDatetime !== newDatetime;

    if (emailChanged) {
      const err = validateEmail(updateEmail);
      if (err) { setEmailError(err); return; }
    }
    setEmailError("");

    if (!emailChanged && !updateResume && !datetimeChanged) {
      message.info("No changes to save.");
      return;
    }

    setIsUpdating(true);
    try {
      const promises = [];

      if (emailChanged) {
        promises.push(
          functionService.post("demand/update-candidate-email", {
            id: candidate.id,
            email: updateEmail.trim().toLowerCase(),
          })
        );
      }

      if (updateResume) {
        const uploadRes = await functionService.uploadFile(updateResume);
        if (!uploadRes?.status) throw new Error("File upload to S3 failed");
        const s3Url = uploadRes.response.data.location;
        promises.push(
          functionService.post("demand/update-candidate-cv", {
            id: candidate.id,
            cv_url: s3Url,
          })
        );
      }

      if (datetimeChanged) {
        promises.push(
          functionService.post("demand/update-assessment-schedule", {
            id: candidate.id,
            assessment_schedule_datetime: newDatetime,
          })
        );
      }

      const results = await Promise.all(promises);
      const allOk = results.every(
        (r) => r?.status === true || r?.response?.data?.meta?.code === 200
      );
      if (!allOk) throw new Error("One or more updates failed");

      const updatedFields = {};
      if (emailChanged) updatedFields.email = updateEmail.trim().toLowerCase();
      if (updateResume) {
        updatedFields.cv_url =
          results[emailChanged ? 1 : 0]?.response?.data?.data?.cv_url || candidate.cv_url;
      }
      if (datetimeChanged) updatedFields.assessment_schedule_datetime = newDatetime;

      onSaved({ id: candidate.id, ...updatedFields });
      setShowSuccess(true);
      setTimeout(() => { setShowSuccess(false); onClose(); }, 1500);
    } catch (error) {
      console.error("Update error:", error);
      message.error("Update failed. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  if (!candidate) return null;

  return (
    <>
      {open && <div style={drawerStyles.overlay} onClick={onClose} />}
      <div style={drawerStyles.panel(open)}>
        <div style={drawerStyles.inner}>
          <div style={drawerStyles.drawerHeader}>
            <div>
              <p style={drawerStyles.drawerTitle}>Update candidate</p>
              <p style={drawerStyles.drawerSubtitle}>ID: {candidate.id}</p>
            </div>
            <button style={drawerStyles.closeBtn} onClick={onClose}>✕</button>
          </div>

          <div style={drawerStyles.candidateChip}>
            <div style={drawerStyles.avatar}>
              {getInitials(candidate.first_name, candidate.last_name)}
            </div>
            <div>
              <p style={{ fontWeight: 600, margin: 0, fontSize: 14, color: "#111" }}>
                {candidate.first_name} {candidate.last_name}
              </p>
              <p style={{ fontSize: 12, color: "#888", margin: "2px 0 0" }}>
                {candidate.company_name} · {candidate.role_title}
              </p>
            </div>
          </div>

          <div style={drawerStyles.section}>
            <label style={drawerStyles.label}>Email address</label>
            <Input
              value={updateEmail}
              onChange={(e) => { setUpdateEmail(e.target.value); setEmailError(""); }}
              placeholder="Enter new email"
              status={emailError ? "error" : ""}
            />
            {emailError
              ? <p style={{ ...drawerStyles.hint, color: "#ef4444" }}>{emailError}</p>
              : <p style={drawerStyles.hint}>Current: {candidate.email}</p>
            }
          </div>

          <div style={drawerStyles.section}>
            <label style={drawerStyles.label}>Assessment Schedule Date &amp; Time</label>
            <DatePicker
              showTime={{ format: "HH:mm", showSecond: false, minuteStep: 5 }}
              format="DD-MM-YYYY HH:mm"
              style={{ width: "100%", height: 36 }}
              value={updateAssessmentDatetime}
              onChange={(date) => setUpdateAssessmentDatetime(date ? date : null)}
              allowClear
              placeholder="Select date & time"
            />
            <p style={drawerStyles.hint}>
              Current:{" "}
              {candidate.assessment_schedule_datetime
                ? dayjs(candidate.assessment_schedule_datetime).format("DD-MM-YYYY HH:mm")
                : <span style={{ color: "#b45309", fontWeight: 600 }}>Not scheduled</span>}
            </p>
          </div>

          <div style={drawerStyles.section}>
            <label style={drawerStyles.label}>Resume / CV</label>
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              style={{ display: "none" }}
              id="resume-upload-input"
            />
            <label htmlFor="resume-upload-input" style={drawerStyles.dropzone(!!updateResume)}>
              <div style={{ fontSize: 28 }}>📄</div>
              <p style={drawerStyles.dropzoneText}>
                {updateResume ? "Click to replace file" : "Drop file here or click to browse"}
              </p>
              <p style={drawerStyles.dropzoneSub}>PDF Only · max 2 MB</p>
            </label>

            {updateResume && (
              <div style={drawerStyles.fileSelectedBadge}>
                <span>✓</span>
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {updateResume.name}
                </span>
                <span style={{ color: "#aaa", fontSize: 11 }}>
                  {(updateResume.size / 1024).toFixed(0)} KB
                </span>
                <button
                  onClick={(e) => { e.preventDefault(); setUpdateResume(null); }}
                  style={{ border: "none", background: "none", cursor: "pointer", color: "#999", fontSize: 14, lineHeight: 1 }}
                >
                  ×
                </button>
              </div>
            )}

            <p style={drawerStyles.hint}>
              {candidate.cv_url
                ? <a href={candidate.cv_url} target="_blank" rel="noreferrer" style={{ color: "#2563eb" }}>View existing resume ↗</a>
                : "No resume uploaded yet"}
            </p>
          </div>

          <div style={drawerStyles.divider} />

          <div style={drawerStyles.actionRow}>
            <Button style={{ flex: 1 }} onClick={onClose} disabled={isUpdating}>Cancel</Button>
            <Button type="primary" style={{ flex: 1 }} loading={isUpdating} onClick={handleSave}>
              {isUpdating ? "Saving…" : "Save changes"}
            </Button>
          </div>

          {showSuccess && (
            <div style={drawerStyles.successBanner}>✓ Candidate updated successfully</div>
          )}
        </div>
      </div>
    </>
  );
};

/* ─────────────────────────────────────────────
   Main CandidatesTable Component
───────────────────────────────────────────── */
const CandidatesTable = () => {
  const [jobListings, setJobListings] = useState([]);
  const [filteredJobListings, setFilteredJobListings] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [roleList, setRoleList] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedCallStatus, setSelectedCallStatus] = useState(null);
  const [selectedReviewStatus, setSelectedReviewStatus] = useState(null);
  const [selectedCompanyShortlistStatus, setSelectedCompanyShortlistStatus] = useState(null);
  const [selectedAssessmentStatus, setSelectedAssessmentStatus] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSourcedDate, setSelectedSourcedDate] = useState(null);
  const [selectedCandidateStatus, setSelectedCandidateStatus] = useState(null);
  const [nameOrEmailSearch, setNameOrEmailSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [modalData, setModalData] = useState({ id: "", email: "", firstName: "", lastName: "" });
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState("");
  const [fieldVal, setFieldVal] = useState("");
  const [candidateStatus, setCandidateStatus] = useState("");
  const [candidateEmail, setCandidateEmail] = useState("");
  const [candidate, setCandidateName] = useState("");
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState({
    interviewerName: "", interviewerEmail: "", interviewDateTime: null,
    interviewDuration: "", interviewLink: "", candidateName: "", candidateEmail: "",
  });
  const [selectedRows, setSelectedRows] = useState([]);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerCandidate, setDrawerCandidate] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [refreshingScoreId, setRefreshingScoreId] = useState(null);

  const [dateRange, setDateRange] = useState(null);
  const { RangePicker } = DatePicker;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleDateChange = (date, dateString) => {
    setFormValues({ ...formValues, interviewDateTime: dateString });
  };

  const getCompanyName = async () => {
    const response = await functionService.get("demand/demand-companies-list");
    if (response?.response?.data?.data?.length > 0) {
      setCompanyList(response?.response?.data?.data);
    }
  };

  useEffect(() => { getCompanyName(); }, []);

  const fetchDetails = async () => {
    try {
      const id = localStorage.getItem("userId");
      const demandDetails = await functionService.getJobListings(id);
      if (demandDetails.status) {
        const jsonData = demandDetails.response.data.data;
        const sortedData = jsonData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setJobListings(sortedData);
        setFilteredJobListings(sortedData);
        setTotalPages(Math.ceil(sortedData.length / itemsPerPage));
        const uniqueRoles = [...new Set(jsonData.map(item => item.role_title).filter(Boolean))].sort();
        setRoleList(uniqueRoles);
      }
    } catch (error) {
      console.error("Error in fetchDetails:", error);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [itemsPerPage]);

  useEffect(() => {
    let f = jobListings;
    if (selectedCompany) f = f.filter((i) => i.company_name === selectedCompany);
    if (selectedRole) f = f.filter((i) => i.role_title === selectedRole);
    if (selectedCallStatus)
      f = f.filter((i) => selectedCallStatus === "Connected" ? i.call_connected : !i.call_connected);
    if (selectedReviewStatus)
      f = f.filter((i) => i.review_status?.toLowerCase() === selectedReviewStatus.toLowerCase());
    if (selectedCompanyShortlistStatus)
      f = f.filter((i) => i.company_shortlist_status?.toLowerCase() === selectedCompanyShortlistStatus.toLowerCase());
    if (selectedAssessmentStatus)
      f = f.filter((i) => selectedAssessmentStatus === "Attempted" ? i.assessment_status : !i.assessment_status);
    if (selectedDate)
      f = f.filter((i) => {
        if (!i.updated_at) return false;
        const d = new Date(i.updated_at);
        return !isNaN(d) && selectedDate.toDate().toDateString() === d.toDateString();
      });
    if (selectedSourcedDate)
      f = f.filter((i) => {
        if (!i.created_at) return false;
        const d = new Date(i.created_at);
        return !isNaN(d) && selectedSourcedDate.toDate().toDateString() === d.toDateString();
      });

    // Sourced From → Sourced To
    if (dateRange && dateRange[0] && dateRange[1]) {
      const startDate = dateRange[0]
        .startOf("day")
        .toDate();

      const endDate = dateRange[1]
        .endOf("day")
        .toDate();

      f = f.filter((i) => {
        if (!i.created_at) return false;

        const sourcedDate = new Date(i.created_at);

        return (
          !isNaN(sourcedDate) &&
          sourcedDate >= startDate &&
          sourcedDate <= endDate
        );
      });
    }

    if (selectedCandidateStatus)
      f = f.filter((i) => i.candidate_current_status === selectedCandidateStatus);
    if (nameOrEmailSearch) {
      const searchText = nameOrEmailSearch.trim().toLowerCase();
      f = f.filter((i) => {
        const fullName = `${i.first_name || ""} ${i.last_name || ""}`.trim().toLowerCase();
        const email = (i.email || "").toLowerCase();
        return fullName.includes(searchText) || email.includes(searchText);
      });
    }

    setFilteredJobListings(f);
    setTotalPages(Math.ceil(f.length / itemsPerPage));
    setCurrentPage(1);
  }, [
    selectedCompany, selectedRole, selectedCallStatus, selectedReviewStatus, selectedCompanyShortlistStatus,
    selectedAssessmentStatus, selectedDate, selectedSourcedDate, dateRange, selectedCandidateStatus, nameOrEmailSearch,
    jobListings, itemsPerPage,
  ]);

  const totalFilteredPages = Math.ceil(filteredJobListings.length / itemsPerPage);
  const currentListings = filteredJobListings.slice(
    (currentPage - 1) * itemsPerPage, currentPage * itemsPerPage
  );

  const buildPageNumbers = () => {
    const pages = [];
    if (totalFilteredPages <= 7) {
      for (let i = 1; i <= totalFilteredPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    if (currentPage > 3) pages.push("...");
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalFilteredPages - 1, currentPage + 1); i++)
      pages.push(i);
    if (currentPage < totalFilteredPages - 2) pages.push("...");
    pages.push(totalFilteredPages);
    return pages;
  };

  const showEmptyState = filteredJobListings.length === 0;

  const handleOpenUpdateDrawer = (item) => { setDrawerCandidate(item); setIsDrawerOpen(true); };

  const handleDrawerSaved = ({ id, email, cv_url, assessment_schedule_datetime }) => {
    const applyUpdate = (prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        const updated = { ...c };
        if (email !== undefined) updated.email = email;
        if (cv_url !== undefined) updated.cv_url = cv_url;
        if (assessment_schedule_datetime !== undefined)
          updated.assessment_schedule_datetime = assessment_schedule_datetime;
        return updated;
      });
    setJobListings(applyUpdate);
    setFilteredJobListings(applyUpdate);
  };

  const refreshTechScore = async (item) => {
    setRefreshingScoreId(item.id);
    try {
      const response = await functionService.post(`demand/refresh-tech-score/${item.id}`, {});
      if (response?.status) {
        await fetchDetails();
      } else {
        console.error("Error refreshing tech score:", response?.error);
        alert("Failed to refresh tech score. Please try again.");
      }
    } catch (error) {
      console.error("Error refreshing tech score:", error);
      alert("Failed to refresh tech score. Please try again.");
    } finally {
      setRefreshingScoreId(null);
    }
  };

  const downloadAssessmentReport = async (item) => {
    setDownloadingId(item.assessment_id);
    try {
      const response = await functionService.getReportWithResume(
        `demand/download-assessment-report/${item.assessment_id}`
      );
      const blob = new Blob([response], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const name = `${item.first_name ? item.first_name + "_" : ""}${item.last_name ? item.last_name + "_" : ""}assessment_report`.replace(/\./g, "");
      a.download = `${name}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading assessment report:", error);
      alert("Failed to download report. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  const menu = (id, assessment_id, email, name, company_shortlist_status) => (
    <Menu onClick={({ key }) => handleMenuClick(key, id, assessment_id, email, name, company_shortlist_status)}>
      <Menu.Item key="Schedule Interview">Schedule interview</Menu.Item>
      <Menu.Item key="View Review Feedback">View review feedback</Menu.Item>
    </Menu>
  );

  const handleMenuClick = (key, id, assessment_id, email, name, company_shortlist_status) => {
    switch (key) {
      case "Schedule Interview":
        setCandidateName(name); setCandidateEmail(email); showModal({ id, key }); break;
      case "View Review Feedback":
        async function getData() {
          try {
            const getStatusRemarks = await functionService.getStatusRemarks({ id });
            if (getStatusRemarks.status) {
              const jsonData = getStatusRemarks.response.data.data;
              setFieldVal(jsonData[0].remarks);
              setCandidateStatus(jsonData[0].company_shortlist_status);
            }
          } catch (error) { console.error("Error fetching remarks:", error); }
        }
        if (company_shortlist_status !== "pending") { getData(); showModal({ id, key }); }
        break;
      default: break;
    }
  };

  const showModal = (data) => { setModalData(data); setModalType(data.key); setIsModalVisible(true); };

  const scheduleInterview = async () => {
    const updatedFormValues = { ...formValues, candidateEmail, candidateName: candidate };
    try {
      await Promise.all([
        functionService.sendEmailCandidate(updatedFormValues),
        functionService.sendEmailInterviewer(updatedFormValues),
      ]);
    } catch (error) { console.error("Error sending mails:", error); }
  };

  const handleOk = async () => { await scheduleInterview(); setIsModalVisible(false); };
  const handleCancel = () => { setIsModalVisible(false); setIsViewModalVisible(false); };

  const profile = {
    name: `${viewData?.first_name} ${viewData?.last_name}`, contact: viewData?.primary_no,
    email: viewData?.email, exp: `${viewData?.total_work_exp / 12} years ${viewData?.total_work_exp % 12} month`,
    salary: viewData?.current_ctc, score: 92,
    imageUrl: "https://media.istockphoto.com/id/513727339/photo/head-and-shoulders-mans-silhouette.jpg",
    cvUrl: viewData?.cv_url,
  };

  const handleIconClick = (data, viewItem) => {
    if (data === "View") { window.open(viewItem.cv_url, "_blank"); }
    else { setModalData({ status: data }); setIsModalVisible(true); }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not available";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return "Invalid date"; }
  };

  const MIN_DATE = dayjs("2026-04-22").startOf("day");
  const disableFutureDates = (current) =>
    current && (current > dayjs().endOf("day") || current < MIN_DATE);

  return (
    <Styled.MainDiv>
      <Sidemenu />
      <Styled.ContentWrapDiv>

        {/* ── Top bar ── */}
        <Styled.TopContainer>
          <div className="leftSide">Candidate Profiles</div>
          <div className="rightSide">
            <button
              onClick={() => navigate("/submission-form")}
              style={{
                height: "40px", padding: "0 18px", borderRadius: "8px", border: "none",
                background: "#4F46E5", color: "#fff", fontWeight: 600, fontSize: "13px",
                cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px",
                boxShadow: "0 1px 4px rgba(79,70,229,0.30)", transition: "background 0.15s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#4338CA")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#4F46E5")}
            >
              + New Submission
            </button>
          </div>
        </Styled.TopContainer>

        {/* ── Filters ── */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            marginBottom: "20px",
            overflowX: "auto",
            flexWrap: "nowrap",          // single line, no wrapping
            paddingBottom: "6px",        // space for scrollbar
            scrollbarWidth: "thin",      // Firefox thin scrollbar
            scrollbarColor: "#D1D5DB transparent",
          }}
        >
          <Select showSearch placeholder="Select a company" optionFilterProp="children"
            onChange={(value) => setSelectedCompany(value)} allowClear style={{ minWidth: "180px", flexShrink: 0 }}>
            {companyList.map((item) => (
              <Select.Option key={item.company_name} value={item.company_name}>{item.company_name}</Select.Option>
            ))}
          </Select>

          <Select showSearch placeholder="Select a role" optionFilterProp="children"
            onChange={(value) => setSelectedRole(value)} allowClear style={{ minWidth: "180px", flexShrink: 0 }}>
            {roleList.map((role) => (
              <Select.Option key={role} value={role}>{role}</Select.Option>
            ))}
          </Select>

          <Select placeholder="Review status" onChange={(value) => setSelectedReviewStatus(value)}
            allowClear style={{ minWidth: "140px", flexShrink: 0 }}>
            <Select.Option value="Pending">Pending</Select.Option>
            <Select.Option value="Approved">Approved</Select.Option>
            <Select.Option value="Rejected">Rejected</Select.Option>
          </Select>

          <Select placeholder="Assessment status" onChange={(value) => setSelectedAssessmentStatus(value)}
            allowClear style={{ minWidth: "160px", flexShrink: 0 }}>
            <Select.Option value="Attempted">Attempted</Select.Option>
            <Select.Option value="Not Attempted">Not Attempted</Select.Option>
          </Select>

          <Select placeholder="Candidate Status" onChange={(value) => setSelectedCandidateStatus(value)}
            allowClear showSearch style={{ minWidth: "200px", flexShrink: 0 }} value={selectedCandidateStatus}>
            {candidateStatusOptions.map((status) => (
              <Select.Option key={status} value={status}>{status}</Select.Option>
            ))}
          </Select>

          <Input
            placeholder="Search by name or email"
            value={nameOrEmailSearch}
            onChange={(e) => setNameOrEmailSearch(e.target.value)}
            allowClear
            style={{ maxWidth: "220px", flexShrink: 0 }}
          />

          <DatePicker placeholder="Reviewed Date" onChange={(date) => setSelectedDate(date || null)}
            format="DD MMM YYYY" allowClear disabledDate={disableFutureDates} style={{ width: "200px", flexShrink: 0 }} />

          {/* <DatePicker placeholder="Sourced Date" onChange={(date) => setSelectedSourcedDate(date || null)}
            format="DD MMM YYYY" allowClear disabledDate={disableFutureDates} style={{ width: "200px", flexShrink: 0 }} /> */}

          {/* <DatePicker
            placeholder="Reviewed Date"
            onChange={(date) => setSelectedDate(date || null)}
            format="DD MMM YYYY"
            allowClear
            disabledDate={disableFutureDates}
            style={{ width: "200px", flexShrink: 0 }}
          /> */}

                    {/* <DatePicker
            placeholder="Sourced Date"
            onChange={(date) => setSelectedSourcedDate(date || null)}
            format="DD MMM YYYY"
            allowClear
            disabledDate={disableFutureDates}
            style={{ width: "200px", flexShrink: 0 }}
          /> */}

          <RangePicker
            value={dateRange}
            onChange={(dates) => {
              setDateRange(dates || null);
            }}
            format="DD MMM YYYY"
            placeholder={[
              "Sourced From",
              "Sourced To",
            ]}
            disabledDate={disableFutureDates}
            separator={
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "24px",
                  minWidth: "24px",
                  flexShrink: 0,
                  color: "#6B7280",
                  fontWeight: 600,
                  fontSize: "16px",
                  lineHeight: "1",
                }}
              >
                →
              </span>
            }
            allowClear
            style={{
              width: "262px",
              minWidth: "262px",
              flexShrink: 0,
            }}
          />
        </div>

        <Styled.TableWrap style={{ overflowX: "auto", overflowY: "auto", maxHeight: "calc(100vh - 260px)" }}>
          {showEmptyState ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", marginTop: "100px" }}>
              <img src={require("../../images/Frame.png")} alt="No Data" style={{ width: "35%", height: "auto" }} />
              <h2 style={{ marginTop: "auto" }}>No Result Found</h2>
            </div>
          ) : (
            // ── REMOVED borderCollapse/borderSpacing — matching ReviewerTable which does NOT set these ──
            <Styled.MainTable style={{ transition: "background-color 0.3s ease-in-out" }}>
              <thead style={{ position: "sticky", top: 0, zIndex: 10, background: "#F2F6F8" }}>
                <tr>
                  {/* Col 1: Checkbox — position sticky left 0, zIndex 11 */}
                  <th style={{ position: "sticky", left: 0, zIndex: 11, background: "#F2F6F8" }}>
                    <input
                      type="checkbox"
                      onChange={(e) => setSelectedRows(e.target.checked ? currentListings : [])}
                      checked={selectedRows.length === currentListings.length && currentListings.length > 0}
                    />
                  </th>
                  {/* Col 2: Review Status — position sticky left 0, zIndex 11 */}
                  <th style={{ position: "sticky", left: 0, zIndex: 11, background: "#F2F6F8" }}>
                    Review status
                  </th>
                  {/* Col 3: Date of Sourcing — position sticky left "50px", zIndex 11 */}
                  <th style={{ position: "sticky", left: "50px", zIndex: 11, background: "#F2F6F8" }}>
                    Date of Sourcing
                  </th>
                  {/* Col 4: Date of Review — position sticky left "100px", zIndex 11 */}
                  <th style={{ position: "sticky", left: "100px", zIndex: 11, background: "#F2F6F8" }}>
                    Date of Review
                  </th>
                  {/* Col 5: Candidate Details — position sticky left "150px", zIndex 11 */}
                  <th style={{ position: "sticky", left: "150px", zIndex: 11, background: "#F2F6F8" }}>
                    Candidate details
                  </th>
                  {/* Non-sticky headers */}
                  <th>Date of Sharing with Client</th>
                  <th>Profile Sent</th>
                  <th>Candidate Current Status</th>
                  <th>Client's Feedback</th>
                  <th>Client's Remark</th>
                  <th>Assessment Schedule</th>
                  <th>Account Name</th>
                  <th>Role</th>
                  <th>Total Exp</th>
                  <th>Relevant Exp</th>
                  <th>Current Company</th>
                  <th>Current Location</th>
                  <th>Preferred Location</th>
                  <th>Current CTC</th>
                  <th>Expected CTC</th>
                  <th>Notice Period</th>
                  <th>Notice Status</th>
                  <th>Last Working Day</th>
                  <th>Available From</th>
                  <th>Offer in Hand (Y/N)</th>
                  <th>Joining Date of Offer</th>
                  <th>Date of Birth</th>
                  <th>Highest Qualification</th>
                  <th>Recruiter's Remarks</th>
                  <th>Reviewer's Remarks</th>
                  <th>Fitment Score</th>
                  <th>Assessment status</th>
                  <th>Tech Score</th>
                  <th>Red Flag</th>
                  <th>Qualify</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentListings.map((item, index) => {
                  const isSelected = selectedRows.includes(item);
                  return (
                    <tr
                      key={index}
                      style={{ backgroundColor: isSelected ? "#d0e7ff" : "transparent", transition: "background-color 0.3s ease-in-out" }}
                    >
                      {/* Col 1: Checkbox — sticky left 0, zIndex 1 */}
                      <td style={{ position: "sticky", left: 0, backgroundColor: isSelected ? "#d0e7ff" : "transparent", zIndex: 1 }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() =>
                            setSelectedRows((prev) =>
                              prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
                            )
                          }
                        />
                      </td>

                      {/* Col 2: Review Status — sticky left 0, zIndex 1 */}
                      <td style={{
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                        background: isSelected
                          ? "#d0e7ff"
                          : item.review_status?.toLowerCase() === "approved"
                            ? "lightgreen"
                            : item.review_status?.toLowerCase() === "rejected"
                              ? "lightcoral"
                              : "white",
                        padding: "5px 10px",
                        borderRadius: "5px",
                        color: "black",
                        transition: "background-color 0.2s ease-in-out",
                      }}>
                        {item.review_status.charAt(0).toUpperCase() + item.review_status.slice(1)}
                      </td>

                      {/* Col 3: Date of Sourcing — sticky left "50px", zIndex 1 */}
                      <td style={{ position: "sticky", left: "50px", backgroundColor: isSelected ? "#d0e7ff" : "white", transition: "background-color 0.2s ease-in-out", zIndex: 1 }}>
                        {new Date(item.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
                      </td>

                      {/* Col 4: Date of Review — sticky left "100px", zIndex 1 */}
                      <td style={{ position: "sticky", left: "100px", backgroundColor: isSelected ? "#d0e7ff" : "white", transition: "background-color 0.2s ease-in-out", zIndex: 1 }}>
                        {item.review_status !== "pending"
                          ? new Date(item.updated_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })
                          : "Pending..."}
                      </td>

                      {/* Col 5: Candidate Details — sticky left "150px", zIndex 1 */}
                      <td style={{ position: "sticky", left: "150px", backgroundColor: isSelected ? "#d0e7ff" : "white", transition: "background-color 0.2s ease-in-out", zIndex: 1 }}>
                        {item.first_name} {item.last_name}<br />{item.email}<br />{item.primary_no}
                      </td>

                      {/* All remaining non-sticky tds */}
                      <td>
                        {item.profile_sent_date
                          ? new Date(item.profile_sent_date).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })
                          : (item.profile_sent === null || item.profile_sent === undefined) ? <PendingBadge /> : "-"}
                      </td>
                      <td>
                        {item.profile_sent === null || item.profile_sent === undefined
                          ? <PendingBadge />
                          : item.profile_sent ? <YesBadge /> : <NoBadge />}
                      </td>
                      <td>
                        {item.candidate_current_status ? item.candidate_current_status : <PendingBadge />}
                      </td>
                      <td>
                        <Tooltip title={item.client_feedback || "-"} placement="topLeft">
                          <span
                            style={{
                              maxWidth: "150px",
                              display: "inline-block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                            }}
                          >
                            {truncateText(item.client_feedback, 25)}
                          </span>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip title={item.client_remark || "-"} placement="topLeft">
                          <span style={{ maxWidth: "150px", display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}>
                            {truncateText(item.client_remark, 25)}
                          </span>
                        </Tooltip>
                      </td>
                      <td>
                        {item.assessment_schedule_datetime
                          ? dayjs(item.assessment_schedule_datetime).format("DD-MM-YYYY HH:mm")
                          : <PendingBadge />}
                      </td>
                      <td>{item.company_name}</td>
                      <td>{item.role_title}</td>
                      <td>{item.total_work_exp ? `${item.total_work_exp}y` : "-"}</td>
                      <td>{item.relevant_experience ? `${item.relevant_experience}y` : "-"}</td>
                      <td>{item.current_company || "-"}</td>
                      <td>{item.candidate_location || "-"}</td>
                      <td>{Array.isArray(item.candidate_prefered_location) ? item.candidate_prefered_location.join(", ") : (item.candidate_prefered_location || "-")}</td>
                      <td>{item.current_ctc ? `${item.current_ctc} LPA` : "-"}</td>
                      <td>{item.expected_ctc ? `${item.expected_ctc} LPA` : "-"}</td>
                      <td>{item.notice_period || "-"}</td>
                      <td>{item.notice_status ? item.notice_status : "-"}</td>
                      <td>{item.last_working_day ? new Date(item.last_working_day).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "-"}</td>
                      <td>{item.available_from ? new Date(item.available_from).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "-"}</td>
                      <td>{item.offer_in_hand ? "Yes - " + item.offered_ctc + " LPA" : "No"}</td>
                      <td>{item.offer_in_hand && item.joining_date_of_offer ? new Date(item.joining_date_of_offer).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "-"}</td>
                      <td>{item.date_of_birth ? new Date(item.date_of_birth).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "-"}</td>
                      <td>{item.highest_qualification || "-"}</td>
                      <td>
                        <Tooltip title={item.remarks || "-"} placement="topLeft">
                          <span style={{ maxWidth: "150px", display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}>
                            {truncateText(item.remarks, 25)}
                          </span>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip title={item.reviewer_remark || "-"} placement="topLeft">
                          <span style={{ maxWidth: "150px", display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}>
                            {truncateText(item.reviewer_remark, 25)}
                          </span>
                        </Tooltip>
                      </td>
                      <td>{item.fitment_score ? `${item.fitment_score}` : "-"}</td>
                      <td>{item.assessment_status?.toUpperCase() === "SUBMITTED" ? "Submitted" : "Not Submitted"}</td>
                      <td>{item.assessment_status?.toUpperCase() === "SUBMITTED" && item.tech_score != null ? `${item.tech_score}%` : "-"}</td>
                      <td style={{
                        color: item.assessment_status?.toUpperCase() !== "SUBMITTED" ? "inherit" : item.red_flag > 0 ? "red" : "green",
                        fontWeight: item.assessment_status?.toUpperCase() === "SUBMITTED" ? "bold" : "normal",
                        textAlign: "center",
                      }}>
                        {item.assessment_status?.toUpperCase() !== "SUBMITTED" ? "-" : item.red_flag ?? 0}
                      </td>
                      <td style={{
                        backgroundColor: item.assessment_status?.toUpperCase() !== "SUBMITTED" ? "transparent" : item.tech_score >= 65 ? "lightgreen" : "lightcoral",
                        fontWeight: item.assessment_status?.toUpperCase() === "SUBMITTED" ? "bold" : "normal",
                        textAlign: "center",
                      }}>
                        {item.assessment_status?.toUpperCase() !== "SUBMITTED" ? "-" : item.tech_score >= 65 ? "Yes" : "No"}
                      </td>
                      <td>
                        <Styled.IconDiv>
                          <Tooltip title="View Resume">
                            <Styled.ViewButton onClick={() => handleIconClick("View", item)} />
                          </Tooltip>
                          {(item.misc_file_url || item.miscellaneous_file) && (
                            <Tooltip title="View Miscellaneous File">
                              <Styled.MiscFileButton
                                onClick={() =>
                                  window.open(item.misc_file_url || item.miscellaneous_file, "_blank")
                                }
                              />
                            </Tooltip>
                          )}
                          {item.assessment_status?.toUpperCase() !== "SUBMITTED" && (
                            <Tooltip title={refreshingScoreId === item.id ? "Refreshing..." : "Refresh Tech Score"}>
                              <Styled.RefreshTechScoreButton
                                loading={refreshingScoreId === item.id}
                                disabled={refreshingScoreId === item.id}
                                onClick={() => refreshTechScore(item)}
                              />
                            </Tooltip>
                          )}
                          {item.assessment_status?.toUpperCase() === "SUBMITTED" && (
                            <Tooltip title={downloadingId === item.assessment_id ? "Downloading..." : "Download Report"}>
                              <Styled.OptionButton
                                onClick={() => downloadingId !== item.assessment_id ? downloadAssessmentReport(item) : null}
                                disabled={downloadingId === item.assessment_id}
                                style={{
                                  opacity: downloadingId !== item.assessment_id ? 1 : 0.5,
                                  pointerEvents: downloadingId !== item.assessment_id ? "auto" : "none",
                                  cursor: downloadingId === item.assessment_id ? "not-allowed" : "pointer",
                                }}
                              />
                            </Tooltip>
                          )}
                          <Tooltip title="Update candidate info">
                            <button
                              style={drawerStyles.editIconBtn}
                              onClick={() => handleOpenUpdateDrawer(item)}
                              onMouseEnter={(e) => (e.currentTarget.style.background = "#1C49EA")}
                              onMouseLeave={(e) => (e.currentTarget.style.background = "#DEE2EF")}
                            >
                              ✏
                            </button>
                          </Tooltip>
                        </Styled.IconDiv>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Styled.MainTable>
          )}
        </Styled.TableWrap>

        {/* ── Pagination ── */}
        {!showEmptyState && (
          <div style={paginationStyles.container}>
            <div style={paginationStyles.leftSection}>
              <span>Show</span>
              <Styled.PaginationSelectWrapper>
                <Select defaultValue={10} size="small" dropdownMatchSelectWidth={false}
                  onChange={(value) => { setItemsPerPage(value); setCurrentPage(1); }}>
                  <Select.Option value={10}>10</Select.Option>
                  <Select.Option value={20}>20</Select.Option>
                  <Select.Option value={50}>50</Select.Option>
                </Select>
              </Styled.PaginationSelectWrapper>
              <span>per page</span>
              <span style={{ marginLeft: "12px", color: "#9CA3AF" }}>|</span>
              <span style={{ marginLeft: "12px" }}>
                {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, filteredJobListings.length)} of {filteredJobListings.length} results
              </span>
            </div>
            <div style={paginationStyles.pageControls}>
              <button style={paginationStyles.navBtn(currentPage === 1)} onClick={() => setCurrentPage((p) => p - 1)} disabled={currentPage === 1}>← Prev</button>
              {buildPageNumbers().map((page, idx) =>
                page === "..." ? (
                  <span key={`ellipsis-${idx}`} style={paginationStyles.ellipsis}>…</span>
                ) : (
                  <button key={page} style={paginationStyles.pageBtn(page === currentPage, false)} onClick={() => setCurrentPage(page)}>{page}</button>
                )
              )}
              <button style={paginationStyles.navBtn(currentPage === totalFilteredPages)} onClick={() => setCurrentPage((p) => p + 1)} disabled={currentPage === totalFilteredPages}>Next →</button>
            </div>
          </div>
        )}

        {/* ── Schedule / Feedback Modal ── */}
        <Modal
          title={modalType === "Schedule Interview" ? "Schedule Interview" : modalType === "View Review Feedback" ? `${candidateStatus} profile` : ""}
          visible={isModalVisible} onCancel={handleCancel} style={{ width: "750px" }}
          footer={[
            <Button key="back" onClick={handleCancel}>Cancel</Button>,
            modalType === "Schedule Interview" && (<Button key="submit" type="primary" onClick={handleOk}>Schedule Interview</Button>),
          ]}
        >
          <Styled.FormDiv>
            {modalType === "Schedule Interview" && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Interviewer Name</label>
                    <Input name="interviewerName" placeholder="Enter interviewer's name" value={formValues.interviewerName} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label>Interviewer Email</label>
                    <Input name="interviewerEmail" placeholder="Enter interviewer's email" value={formValues.interviewerEmail} onChange={handleInputChange} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Interview Date &amp; Time</label>
                    <DatePicker showTime placeholder="Select date & time" style={{ width: "100%" }} onChange={handleDateChange} />
                  </div>
                  <div className="form-group">
                    <label>Interview Duration</label>
                    <Input name="interviewDuration" placeholder="Enter duration (in minutes)" value={formValues.interviewDuration} onChange={handleInputChange} />
                  </div>
                </div>
                <label>Remarks</label>
                <Input.TextArea name="interviewLink" placeholder="Enter remarks (if any)" style={{ width: "100%", height: "150px" }} value={formValues.interviewLink} onChange={handleInputChange} />
              </>
            )}
            {modalType === "View Review Feedback" && (
              <>
                <label>Remarks</label>
                <Input.TextArea placeholder="Company Remarks" style={{ width: "100%", height: "150px" }} value={fieldVal} />
              </>
            )}
          </Styled.FormDiv>
        </Modal>

        {/* ── Profile View Modal ── */}
        <Modal visible={isViewModalVisible} onCancel={handleCancel} footer={[<Button key="back" onClick={handleCancel}>Close</Button>]}>
          <div><ProfileComp profile={profile} /></div>
        </Modal>

      </Styled.ContentWrapDiv>

      <UpdateCandidateDrawer
        open={isDrawerOpen}
        candidate={drawerCandidate}
        onClose={() => setIsDrawerOpen(false)}
        onSaved={handleDrawerSaved}
      />

    </Styled.MainDiv>
  );
};

export default CandidatesTable;